import React, { useState, useEffect } from 'react';
import { getData } from '../firebase';

const BRAND = '#0f6e56';

export default function BalanceSheet({ user }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      const [invoices, expenses, pettyCash, bankBook] = await Promise.all([
        getData(user.uid, 'invoices'),
        getData(user.uid, 'expenses'),
        getData(user.uid, 'pettycash'),
        getData(user.uid, 'bankbook'),
      ]);
      const cashIn = pettyCash.filter(p => p.type === 'in').reduce((s, p) => s + p.amount, 0);
      const cashOut = pettyCash.filter(p => p.type === 'out').reduce((s, p) => s + p.amount, 0);
      const cashOnHand = Math.max(0, cashIn - cashOut);
      const bankDep = bankBook.filter(b => b.type === 'deposit').reduce((s, b) => s + b.amount, 0);
      const bankWith = bankBook.filter(b => b.type === 'withdrawal').reduce((s, b) => s + b.amount, 0);
      const bankBalance = Math.max(0, bankDep - bankWith);
      const accountsReceivable = invoices.filter(i => i.type === 'sales' && i.status === 'unpaid').reduce((s, i) => s + i.amount, 0);
      const accountsPayable = invoices.filter(i => i.type === 'purchase' && i.status === 'unpaid').reduce((s, i) => s + i.amount, 0);
      const totalRevenue = invoices.filter(i => i.type === 'sales' && i.status === 'paid').reduce((s, i) => s + i.amount, 0);
      const totalCOGS = invoices.filter(i => i.type === 'purchase').reduce((s, i) => s + i.amount, 0);
      const totalExp = expenses.reduce((s, e) => s + e.amount, 0);
      const retainedEarnings = totalRevenue - totalCOGS - totalExp;
      const totalCurrentAssets = cashOnHand + bankBalance + accountsReceivable;
      const totalLiabilities = accountsPayable;
      const totalEquity = retainedEarnings;
      setData({ cashOnHand, bankBalance, accountsReceivable, totalCurrentAssets, totalAssets: totalCurrentAssets, accountsPayable, totalLiabilities, retainedEarnings, totalEquity, totalLiabAndEq: totalLiabilities + totalEquity, balanced: Math.abs(totalCurrentAssets - (totalLiabilities + totalEquity)) < 0.01, asOf: new Date().toLocaleDateString('en-US', { year:'numeric', month:'long', day:'numeric' }) });
      setLoading(false);
    };
    load();
  }, [user.uid]);

  const fmt = n => '$' + Number(n || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  if (loading) return <div style={{ textAlign: 'center', padding: 60, color: '#aaa' }}>Loading...</div>;

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
        <h2 style={{ fontSize: 22, fontWeight: 700 }}>Balance Sheet</h2>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <span style={{ fontSize: 13, color: '#888' }}>As of {data.asOf}</span>
          {data.balanced
            ? <span style={{ background: '#e1f5ee', color: BRAND, padding: '4px 12px', borderRadius: 20, fontSize: 12, fontWeight: 600 }}>✓ Balanced</span>
            : <span style={{ background: '#fcebeb', color: '#a32d2d', padding: '4px 12px', borderRadius: 20, fontSize: 12, fontWeight: 600 }}>Check entries</span>
          }
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20 }}>
        <div>
          <BSSection title="ASSETS" color={BRAND} bg="#e1f5ee" icon="📈">
            <BSSubHead label="Current Assets" />
            <BSRow label="Cash on hand" value={fmt(data.cashOnHand)} note="From petty cash" />
            <BSRow label="Bank balance" value={fmt(data.bankBalance)} note="From bank book" />
            <BSRow label="Accounts receivable" value={fmt(data.accountsReceivable)} note="Unpaid sales invoices" />
            <BSRow label="Total Current Assets" value={fmt(data.totalCurrentAssets)} bold />
            <BSRow label="TOTAL ASSETS" value={fmt(data.totalAssets)} total />
          </BSSection>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <BSSection title="LIABILITIES" color="#a32d2d" bg="#fcebeb" icon="📤">
            <BSSubHead label="Current Liabilities" />
            <BSRow label="Accounts payable" value={fmt(data.accountsPayable)} note="Unpaid purchase invoices" />
            <BSRow label="TOTAL LIABILITIES" value={fmt(data.totalLiabilities)} total />
          </BSSection>

          <BSSection title="EQUITY" color="#534ab7" bg="#eeedfb" icon="🏛">
            <BSRow label="Retained earnings" value={fmt(data.retainedEarnings)} note="Revenue minus all costs" />
            <BSRow label="TOTAL EQUITY" value={fmt(data.totalEquity)} total />
          </BSSection>

          <div style={{ background: data.balanced ? '#e1f5ee' : '#fcebeb', borderRadius: 12, padding: '16px 20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span style={{ fontWeight: 700, color: data.balanced ? BRAND : '#a32d2d' }}>Total Liabilities + Equity</span>
            <span style={{ fontWeight: 700, fontSize: 20, color: data.balanced ? BRAND : '#a32d2d' }}>{fmt(data.totalLiabAndEq)}</span>
          </div>

          <div style={{ background: '#fff', borderRadius: 12, padding: 16, border: '1px solid #e8e8e8', textAlign: 'center' }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: '#534ab7', marginBottom: 6 }}>Assets = Liabilities + Equity</div>
            <div style={{ fontSize: 12, color: '#888' }}>{fmt(data.totalAssets)} = {fmt(data.totalLiabilities)} + {fmt(data.totalEquity)}</div>
          </div>
        </div>
      </div>
    </div>
  );
}

function BSSection({ title, color, bg, icon, children }) {
  return (
    <div style={{ background: '#fff', borderRadius: 12, border: '1px solid #e8e8e8', overflow: 'hidden' }}>
      <div style={{ background: bg, padding: '12px 16px', display: 'flex', alignItems: 'center', gap: 8 }}>
        <span>{icon}</span>
        <span style={{ fontSize: 13, fontWeight: 700, color }}>{title}</span>
      </div>
      {children}
    </div>
  );
}

function BSSubHead({ label }) {
  return <div style={{ padding: '6px 16px', fontSize: 11, color: '#888', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5, borderTop: '1px solid #f0f0f0' }}>{label}</div>;
}

function BSRow({ label, value, bold, total, note }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', padding: '10px 16px', borderTop: '1px solid #f0f0f0', background: total ? '#f7f8fa' : 'transparent' }}>
      <div style={{ flex: 1 }}>
        <div style={{ fontSize: 13, color: total || bold ? '#222' : '#555', fontWeight: total || bold ? 700 : 400 }}>{label}</div>
        {note && <div style={{ fontSize: 11, color: '#aaa', marginTop: 1 }}>{note}</div>}
      </div>
      <div style={{ fontSize: total ? 15 : 13, fontWeight: total || bold ? 700 : 400, color: '#222' }}>{value}</div>
    </div>
  );
}
